import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link2, CheckCircle2, AlertCircle, Settings, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "X (Twitter)",
  google_business: "Google Business",
  youtube: "YouTube",
  threads: "Threads",
  pinterest: "Pinterest",
};

const META_PLATFORMS = ["facebook", "instagram"];

export default function SocialConnect() {
  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState([]);
  const [showSettings, setShowSettings] = useState(null);
  const [showGbpForm, setShowGbpForm] = useState(false);
  const [gbpForm, setGbpForm] = useState({ access_token: "", location_id: "", location_name: "" });

  const load = async () => {
    const [{ data: a }, { data: s }] = await Promise.all([
      api.get("/social/accounts"),
      api.get("/channels/settings"),
    ]);
    setAccounts(a);
    setSettings(s);
  };

  useEffect(() => { load(); }, []);

  // Handle OAuth return params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("meta_connected")) {
      const pages = params.get("pages") || "0";
      toast.success(`Meta connected — ${pages} page(s) linked`);
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else if (params.get("meta_error")) {
      toast.error(`Meta connect failed: ${params.get("meta_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectMeta = async () => {
    try {
      const { data } = await api.get("/meta/oauth/url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Meta integration is not configured by admin yet.");
    }
  };

  const disconnectMeta = async () => {
    if (!window.confirm("Disconnect Facebook + Instagram?")) return;
    await api.delete("/meta/disconnect");
    toast.success("Meta disconnected");
    await load();
  };

  const toggle = async (acc) => {
    if (acc.platform === "google_business" && !acc.connected) {
      setShowGbpForm(true);
      return;
    }
    if (META_PLATFORMS.includes(acc.platform)) {
      if (acc.connected && !acc.mock) {
        await disconnectMeta();
      } else {
        await connectMeta();
      }
      return;
    }
    if (acc.connected) {
      await api.post("/social/disconnect", { platform: acc.platform });
      toast.success(`Disconnected ${LABELS[acc.platform]}`);
    } else {
      await api.post("/social/connect", { platform: acc.platform });
      toast.success(`Connected ${LABELS[acc.platform]} (mock)`);
    }
    await load();
  };

  const connectGbp = async () => {
    if (!gbpForm.access_token || !gbpForm.location_id) {
      toast.error("Token + location id required");
      return;
    }
    try {
      await api.post("/gbp/connect", gbpForm);
      toast.success("Google Business Profile connected — try Sync reviews");
      setShowGbpForm(false);
      setGbpForm({ access_token: "", location_id: "", location_name: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
    }
  };

  const syncReviews = async () => {
    try {
      const { data } = await api.post("/gbp/sync-reviews", {});
      toast.success(`Imported ${data.imported} of ${data.total} reviews into Inbox`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
  };

  const settingFor = (p) => settings.find((s) => s.platform === p) || {};

  const saveSetting = async (platform, patch) => {
    const cur = settingFor(platform);
    const body = {
      platform,
      auto_publish: cur.auto_publish ?? false,
      require_approval: cur.require_approval ?? true,
      sla_minutes: cur.sla_minutes ?? 60,
      auto_reply: cur.auto_reply ?? false,
      ...patch,
    };
    await api.put("/channels/settings", body);
    await load();
  };

  return (
    <div className="space-y-8" data-testid="social-connect-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Connections</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Connect your channels</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Plug in your social accounts and tune per-channel auto-publish + SLA timers.
        </p>
      </header>

      <div className="glass rounded-2xl p-4 border border-amber-500/30 bg-amber-500/[0.04]">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-white/80">
            <span className="font-semibold text-amber-400">LIVE integrations: </span>
            Facebook + Instagram (Meta OAuth — your admin must set the App ID in Admin → Integrations first), and Google Business Profile (paste-token flow). All other platforms remain mocked.
          </div>
        </div>
      </div>

      {showGbpForm && (
        <div className="glass rounded-2xl p-6 lg:p-8" data-testid="gbp-form">
          <h3 className="font-heading text-xl font-bold">Connect Google Business Profile</h3>
          <p className="text-sm text-white/60 mt-2">
            Get an access token from <a href="https://developers.google.com/oauthplayground" target="_blank" rel="noreferrer" className="brand-gradient-text underline">Google OAuth Playground</a> with scope <code className="font-mono text-xs">https://www.googleapis.com/auth/business.manage</code>. Find your location id under <code className="font-mono text-xs">accounts/&#123;id&#125;/locations/&#123;id&#125;</code>.
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Access token</label>
              <input
                value={gbpForm.access_token}
                onChange={(e) => setGbpForm({ ...gbpForm, access_token: e.target.value })}
                data-testid="gbp-token-input"
                placeholder="ya29.A0..."
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Location id</label>
              <input
                value={gbpForm.location_id}
                onChange={(e) => setGbpForm({ ...gbpForm, location_id: e.target.value })}
                data-testid="gbp-location-input"
                placeholder="accounts/123/locations/456"
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Label (optional)</label>
              <input
                value={gbpForm.location_name}
                onChange={(e) => setGbpForm({ ...gbpForm, location_name: e.target.value })}
                placeholder="Main location"
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={connectGbp} data-testid="gbp-connect-submit" className="brand-gradient px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90">Connect</button>
            <button onClick={() => setShowGbpForm(false)} className="glass px-5 py-2.5 rounded-lg text-sm hover:bg-white/5">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((a) => {
          const s = settingFor(a.platform);
          const isGbpLive = a.platform === "google_business" && a.connected && !a.mock;
          const isMetaLive = META_PLATFORMS.includes(a.platform) && a.connected && !a.mock;
          const isLive = isGbpLive || isMetaLive;
          return (
            <div
              key={a.platform}
              className={`glass rounded-2xl p-6 ${a.connected ? "border-orange-500/40" : ""}`}
              data-testid={`social-card-${a.platform}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading font-bold text-lg">{LABELS[a.platform]}</div>
                {a.connected ? <CheckCircle2 className="h-5 w-5 text-orange-500" /> : <Link2 className="h-5 w-5 text-white/30" />}
              </div>
              <div className="text-xs text-white/50">
                {a.connected ? (
                  <span className={isLive ? "text-green-400" : ""}>{isLive ? "LIVE connection" : "Connected (mock)"}</span>
                ) : "Not connected"}
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <button
                  onClick={() => toggle(a)}
                  data-testid={`social-toggle-${a.platform}`}
                  className={`w-full px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 ${a.connected ? "glass hover:bg-white/5" : "brand-gradient hover:opacity-90"}`}
                >
                  {a.connected ? "Disconnect" : (
                    META_PLATFORMS.includes(a.platform) ? <>Connect with Meta <ExternalLink className="h-3.5 w-3.5" /></> :
                    a.platform === "google_business" ? "Connect (live)" : "Connect (mock)"
                  )}
                </button>
                {isGbpLive && (
                  <button
                    onClick={syncReviews}
                    data-testid="gbp-sync-button"
                    className="w-full glass px-4 py-2 rounded-lg text-sm hover:bg-white/5 inline-flex items-center justify-center gap-2"
                  >
                    <Download className="h-3.5 w-3.5" /> Sync reviews to inbox
                  </button>
                )}
                <button
                  onClick={() => setShowSettings(showSettings === a.platform ? null : a.platform)}
                  data-testid={`channel-settings-${a.platform}`}
                  className="w-full text-xs text-white/60 hover:text-white inline-flex items-center justify-center gap-1.5 py-1"
                >
                  <Settings className="h-3.5 w-3.5" /> Channel settings
                </button>
              </div>

              {showSettings === a.platform && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-3" data-testid={`settings-panel-${a.platform}`}>
                  <label className="flex items-center justify-between text-xs cursor-pointer">
                    <span className="text-white/70">Auto-publish posts</span>
                    <input type="checkbox" checked={s.auto_publish || false} onChange={(e) => saveSetting(a.platform, { auto_publish: e.target.checked })} data-testid={`toggle-autopublish-${a.platform}`} className="accent-orange-500" />
                  </label>
                  <label className="flex items-center justify-between text-xs cursor-pointer">
                    <span className="text-white/70">Require approval</span>
                    <input type="checkbox" checked={s.require_approval ?? true} onChange={(e) => saveSetting(a.platform, { require_approval: e.target.checked })} data-testid={`toggle-approval-${a.platform}`} className="accent-orange-500" />
                  </label>
                  <label className="flex items-center justify-between text-xs cursor-pointer">
                    <span className="text-white/70">Auto-reply (non-sensitive)</span>
                    <input type="checkbox" checked={s.auto_reply || false} onChange={(e) => saveSetting(a.platform, { auto_reply: e.target.checked })} data-testid={`toggle-autoreply-${a.platform}`} className="accent-orange-500" />
                  </label>
                  <div>
                    <div className="text-xs text-white/60 mb-1">SLA: auto-send after {s.sla_minutes ?? 60} min</div>
                    <input type="range" min={5} max={1440} step={5} value={s.sla_minutes ?? 60} onChange={(e) => saveSetting(a.platform, { sla_minutes: parseInt(e.target.value) })} data-testid={`sla-slider-${a.platform}`} className="w-full accent-orange-500" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
